import * as React from 'react';
import {
  Body,
  BottomSheet,
  Loading as BaseLoading,
  Input,
  Row,
  styled,
  Flex,
  useTheme,
} from '@teko/ds-mobile';
import {
  BackOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
} from '@teko/ds-icon';
import { PopupThreeLevelLocationPickerProps } from '..';
import { useRef } from 'react';
import { useLevelThreeLevelLocation } from 'hooks/useThreeLevelLocation';
import { DISTRICT, PROVINCE, WARD } from 'constants/threeLevelLocation';
import { t } from 'i18n';

export const PopupThreeLevelLocationPickerMobile: React.FC<
  PopupThreeLevelLocationPickerProps
> = ({
  showPopup,
  onClosePopup,
  threeLevelLocation,
  updateThreeLevelLocation,
  stepLocation,
  setStepLocation,
  onSubmitNewLocation,
  correctThreeLevelLocation,
}) => {
  const listRef = useRef<HTMLDivElement>();
  const displayBackButton = stepLocation === DISTRICT || stepLocation === WARD;
  const {
    filterLocations,
    loadingLocation,
    onSelectLocation,
    backToPreviousLevelLocation,
    headerText,
    keyword,
    setKeyword,
  } = useLevelThreeLevelLocation({
    threeLevelLocation,
    stepLocation,
    updateThreeLevelLocation,
    setStepLocation,
    onClosePopup,
    onSubmitNewLocation,
  });
  const theme = useTheme();

  return (
    <BottomSheet
      show={showPopup}
      position={'bottom'}
      zIndex={2000}
      backdropClicked={onClosePopup}
    >
      <DefaultContent>
        <Flex
          width="100%"
          direction="row"
          align="center"
          justify="space-around"
          marginTop="s8"
        >
          <BackOutlined
            size={28}
            onClick={
              displayBackButton ? backToPreviousLevelLocation : undefined
            }
            color={
              displayBackButton ? theme.colors.placeholder : theme.colors.white
            }
          />
          <Flex
            direction="column"
            align="center"
            marginLeft="s8"
            marginRight="s8"
          >
            <Title>{headerText.title}</Title>
            <SubTitle>{headerText.sutTitle}</SubTitle>
          </Flex>
          <CloseOutlined
            color={
              !!correctThreeLevelLocation
                ? theme.colors.placeholder
                : theme.colors.white
            }
            size={28}
            onClick={onClosePopup}
          />
        </Flex>
        <Row>
          <StyledInput
            prefix={<SearchOutlined />}
            placeholder={headerText.placeHolderSearch}
            value={keyword}
            onChange={setKeyword}
          />
        </Row>
        <List
          ref={(ref) => {
            if (!ref) {
              return;
            }
            // remove right padding when scrollbar is visible
            if (ref.scrollHeight > ref.clientHeight) {
              ref.style.paddingRight = '0';
            }

            listRef.current = ref;
          }}
        >
          {filterLocations &&
          Array.isArray(filterLocations) &&
          filterLocations.length ? (
            filterLocations.map((location) => {
              const isSelected =
                (stepLocation === PROVINCE &&
                  location.value === threeLevelLocation.province.code) ||
                (stepLocation === DISTRICT &&
                  location.value === threeLevelLocation.district?.code) ||
                (stepLocation === WARD &&
                  location.value === threeLevelLocation.ward.code);
              return (
                <ListItem
                  key={location.value}
                  onClick={() => {
                    onSelectLocation({
                      code: location.value,
                      label: location.label,
                      id: location.id,
                    });
                    setKeyword('');
                  }}
                  active={isSelected}
                >
                  {location.label}
                  {isSelected && (
                    <CheckOutlined color="primary500" size="1.5rem" />
                  )}
                </ListItem>
              );
            })
          ) : (
            <Row justify="center" align="middle">
              {loadingLocation ? (
                <BaseLoading isLoading={loadingLocation} />
              ) : (
                <Body color="placeholder" level={1} weight="regular">
                  {t('Không tìm thấy dữ liệu nào !!')}
                </Body>
              )}
            </Row>
          )}
        </List>
      </DefaultContent>
    </BottomSheet>
  );
};

const DefaultContent = styled.div`
  width: 100vw;
  min-height: 60vh;
  text-align: center;
  color: #000000;
`;

const Title = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #000000;
`;

const SubTitle = styled.span`
  width: 100%;
  color: #82869e;
  font-size: 13px;
  font-weight: 400;
`;

const StyledInput = styled(Input)`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background};
  padding: 0 1rem;
  border-radius: 4px;
  margin: 15px;
  .input-container {
    border: none;
  }
`;

const List = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  max-height: min(100vh, 28rem);
  width: 100%;
  padding-bottom: 0.75rem;
  ::-webkit-scrollbar {
    width: 0.75rem;
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.disable};
    background-clip: content-box;
    border-radius: 100px;
    border: 0.25rem solid transparent;
    width: 0.25rem;
  }

  ::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colors.placeholder};
  }
`;

const ListItem = styled.div<{ active?: boolean }>`
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  flex-direction: row;
  height: 3rem;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background-color: ${({ theme, active }) =>
    active ? theme.colors.background : theme.colors.white};
`;
